/**
 * Adaptador de TECLADO. Rellena el mismo `InputState` que el mando táctil
 * (`controls/touch.ts`), de modo que el juego no sabe de dónde le llega el input.
 *
 * MOB-3 · Las teclas ya no están escritas en un `switch`: salen de un mapa
 * remapeable (`controls/keybinds.ts`). El mapa se puede cambiar en caliente sin
 * volver a enganchar los eventos, porque quien manda es el índice invertido que
 * se reconstruye al asignar.
 */
import { InputState } from "@tak/sim";
import {
  Action, KeyBindings, defaultBindings, buildKeyIndex, normalizeKey, shouldPreventDefault,
} from "./controls/keybinds";

export interface KeyboardInput {
  /** Cambia el mapa de teclas en caliente (tras editarlo en los ajustes). */
  setBindings(binds: KeyBindings): void;
  /** Suelta todo lo pulsado. */
  releaseAll(): void;
  /** Desengancha los escuchas. */
  detach(): void;
}

export function attachInput(
  target: Window,
  input: InputState,
  isBlocked?: () => boolean,
  initialBindings: KeyBindings = defaultBindings(),
): KeyboardInput {
  /** Teclas ahora mismo pulsadas, ya normalizadas. */
  const downKeys = new Set<string>();
  let index = buildKeyIndex(initialBindings);

  const apply = (action: Action, pressed: boolean): void => {
    if (action === "kick") {
      // Soltar dispara la patada cargada (misma semántica que en táctil).
      if (!pressed && input.kick) input.kickRelease = true;
      input.kick = pressed;
      return;
    }
    input[action] = pressed;
  };

  const releaseAll = (): void => {
    for (const k of downKeys) {
      const action = index.get(k);
      if (action) apply(action, false);
    }
    downKeys.clear();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    // Mientras se escribe en el chat, el juego ignora el teclado.
    if (isBlocked && isBlocked()) { releaseAll(); return; }
    const k = normalizeKey(e.key);
    const action = index.get(k);
    if (!action) return;
    downKeys.add(k);
    apply(action, true);
    if (shouldPreventDefault(e.key)) e.preventDefault();
  };

  const onKeyUp = (e: KeyboardEvent): void => {
    const k = normalizeKey(e.key);
    downKeys.delete(k);
    const action = index.get(k);
    if (action) apply(action, false);
  };

  const onBlur = (): void => releaseAll();

  target.addEventListener("keydown", onKeyDown);
  target.addEventListener("keyup", onKeyUp);
  window.addEventListener("blur", onBlur);

  return {
    setBindings(binds: KeyBindings): void {
      // Se sueltan las teclas ANTES de cambiar el índice: si no, una tecla
      // pulsada con el mapa viejo ya no se encontraría al soltarla y se quedaría
      // marcada como pulsada para siempre (el disco corriendo solo).
      releaseAll();
      index = buildKeyIndex(binds);
    },
    releaseAll,
    detach(): void {
      target.removeEventListener("keydown", onKeyDown);
      target.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    },
  };
}
